<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-bold text-gray-800">Company Culture</h2>
      <button
        class="bg-brand-black p-3 rounded-xl text-white cursor-pointer disabled:bg-opacity-10 disabled:cursor-not-allowed"
        @click="handleSave"
        :disabled="!canSave || isLoading"
      >
        Save Changes
      </button>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card v-for="(value, index) in cultureValues" :key="index" class="relative group" v-if="cultureValues.length >= 1">
        <CardHeader class="pb-2">
          <CardTitle class="flex items-center">
            <span class="text-2xl mr-2">{{ value.icon }}</span>
            {{ value.title }}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p class="text-gray-600">{{ value.description }}</p>
        </CardContent>
        <CardFooter>
          <Button
            variant="destructive"
            size="sm"
            class="opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2"
            @click="handleRemoveValue(index)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </CardFooter>
      </Card>
      <div v-else class="col-span-3 text-center font-semibold text-xl border py-5 rounded-md">
        <p>Add company culture</p>
      </div>
    </div>

    <Card class="mt-6">
      <CardHeader>
        <CardTitle>Add New Culture Value</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label for="title">Value Title</Label>
            <Input id="title" v-model="newValue.title" placeholder="e.g., COLLABORATIVE" />
          </div>

          <div>
            <Label for="icon">Icon (Emoji)</Label>
            <Input id="icon" v-model="newValue.icon" placeholder="e.g., 🤝" />
          </div>

          <div class="md:col-span-3">
            <Label for="description">Description</Label>
            <Textarea id="description" v-model="newValue.description" placeholder="Describe this cultural value" class="resize-none" rows="2" />
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <button
          class="bg-brand-black p-3 rounded-xl text-white cursor-pointer flex items-center disabled:bg-opacity-10 disabled:cursor-not-allowed"
          @click="handleAddValue"
          :disabled="isDisabled"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-2"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Culture Value
        </button>
      </CardFooter>
    </Card>

    <Card class="mt-6">
      <CardHeader>
        <CardTitle>Gallery</CardTitle>
      </CardHeader>
      <CardContent>
        <!-- Upload Area -->
        <div
          class="border-2 border-dashed border-gray-300 rounded-md p-6 text-center cursor-pointer hover:bg-gray-50"
          @click="triggerFileInput"
          @dragover.prevent
          @drop.prevent="handleDrop"
        >
          <p class="text-sm text-gray-500">Click to upload or drag and drop images</p>
          <input ref="fileInput" type="file" multiple @change="uploadGalleryImages" class="hidden" accept="image/*" />
        </div>

        <!-- Preview Gallery -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <!-- Preview Images (not uploaded yet) -->
          <div v-for="(image, index) in galleryPreview" :key="'preview-' + index" class="relative group opacity-50">
            <img :src="image.image_url" alt="Preview" class="w-full h-40 object-cover rounded-md border" />
          </div>

          <!-- Uploaded Images -->
          <div v-for="(image, index) in galleries" :key="'uploaded-' + index" class="relative group">
            <img :src="image.image_url" alt="Gallery Image" class="w-full h-40 object-cover rounded-md border" />
            <Button
              variant="destructive"
              size="sm"
              class="opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2 z-10"
              @click.stop="removeGalleryImage(index)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
    <Loading :isLoading="isLoading" className="fixed inset-0 bg-[#1B224B]/[.2] flex justify-center items-center" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import Button from '../ui/Button.vue';
import Input from '../ui/Input.vue';
import Textarea from '../ui/Textarea.vue';
import Label from '../ui/Label.vue';
import Card from '../ui/Card.vue';
import CardHeader from '../ui/CardHeader.vue';
import CardTitle from '../ui/CardTitle.vue';
import CardContent from '../ui/CardContent.vue';
import CardFooter from '../ui/CardFooter.vue';
import { useStore } from 'vuex';
import Loading from '@/components/Loading.vue';

const store = useStore();
const props = defineProps({ companyDetails: { type: Object } });
const emit = defineEmits(['refreshCompanyProfile']);


const cultureValues = ref([]);
const newCultureItems = ref([]);
const galleries = ref([]);
const newGalleryItems = ref([]);
const canSave = ref(false);
const isLoading = ref(false);
const fileInput = ref(null);
const newValue = reactive({ title: '', description: '', icon: '' });
const galleryPreview = ref([]);

const isDisabled = computed(() => {
  return !(newValue.title && newValue.icon && newValue.description);
});

const handleAddValue = () => {
  if (newValue.title && newValue.description && newValue.icon) {
    const value = { ...newValue };
    cultureValues.value.push(value);
    newCultureItems.value.push(value);
    newValue.title = '';
    newValue.icon = '';
    newValue.description = '';
  }
};

const handleRemoveValue = (id) => {
  cultureValues.value.splice(id, 1);
};

const triggerFileInput = () => {
  fileInput.value.click();
};

const uploadGalleryImages = async (event) => {
  const files = Array.from(event.target.files);
  await processImages(files);
};

const handleDrop = async (event) => {
  const files = Array.from(event.dataTransfer.files);
  await processImages(files);
};

const processImages = async (files) => {
  for (const file of files) {
    const reader = new FileReader();

    reader.onload = (e) => {
      galleryPreview.value.push({ image_url: e.target.result });
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);

    try {
      isLoading.value = true;
      const imageUrl = await store.dispatch('global/uploadFileUrl', formData);
      const imageObj = { image_url: imageUrl };

      // Add to final galleries and track new ones
      galleries.value.push(imageObj);
      newGalleryItems.value.push(imageObj);

      // Remove the base64 preview once uploaded
      galleryPreview.value = galleryPreview.value.filter((preview) => preview.image_url !== reader.result);
    } catch (err) {
      console.error('Upload failed:', err.response?.data || err.message);
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      isLoading.value = false;
    }
  }
};

const removeGalleryImage = (index) => {
  galleries.value.splice(index, 1);
};

const handleSave = async () => {
  isLoading.value = true;
  try {
    if (newCultureItems.value.length > 0) {
      await store.dispatch('auth/updateUserProfile', { cultures: newCultureItems.value });
      newCultureItems.value = [];
    }
    if (newGalleryItems.value.length > 0) {
      await store.dispatch('auth/updateUserProfile', { galleries: newGalleryItems.value });
      newGalleryItems.value = [];
    }
    emit('refreshCompanyProfile');
  } catch (error) {
    console.error('Error saving company info:', error);
  } finally {
    canSave.value = false;
    isLoading.value = false;
  }
};

onMounted(() => {
  if (props.companyDetails?.cultures?.length) {
    cultureValues.value = [...props.companyDetails.cultures];
  }
  if (props.companyDetails?.galleries?.length) {
    galleries.value = [...props.companyDetails.galleries];
  }
});

watch(
  () => cultureValues.value.length,
  (newLength, oldLength) => {
    if (newLength !== oldLength) canSave.value = true;
  },
  { flush: 'post' },
);

watch(
  () => galleries.value.length,
  (newLength, oldLength) => {
    if (newLength !== oldLength) canSave.value = true;
  },
  { flush: 'post' },
);
</script>
